import cn from "classnames"

import { Divider, Flex, Text } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { ExternalLinkOutlined } from "@/icons"
import { CodeHighlight } from "@/molecules/CodeHighlight"
import { LinkButton } from "@/molecules/LinkButton"
import { LabelCollapse } from "./LabelCollapse"

import styles from "./Flow.module.scss"

import type { BodyCollapseProps } from "./Flow.types"

export const BodyCollapse = ({ item }: BodyCollapseProps) => {
  const moreThanOneLogicalAction = (item.logicalActions?.length ?? 0) > 1
  const { t } = useTranslation()
  const isLinkToCode = !!item.scmUrl

  return (
    <Flex vertical gap={16}>
      <LabelCollapse
        title={t("general.description")}
        description={item.summary}
        variant="secondary"
      />
      <LabelCollapse
        title={t("triage.drawer.dataFlow.functionSignature")}
        description={item.fullName}
        variant="secondary"
      />
      <LabelCollapse
        title={t("triage.drawer.dataFlow.logicalActions")}
        variant="secondary"
      >
        <Flex vertical gap={4}>
          <Flex
            vertical
            gap={16}
            className={cn(styles.logicalActionsWrapper, {
              [styles.logicalActionsWrapperMany]: moreThanOneLogicalAction
            })}
          >
            {item.logicalActions?.map((action, actionIndex: number) => (
              <Flex vertical key={actionIndex} gap={4}>
                {action.summary && (
                  <Text className={cn(styles.text, styles.text8)}>
                    {action.summary}
                  </Text>
                )}
                <CodeHighlight
                  card
                  lineNumbers
                  scrollable
                  variant="compact"
                  startLineNumber={action.startLine}
                  theme="dark"
                  language="unknown"
                  code={action.snippet
                    .map((item) => item.content || "")
                    .join("\n")
                    .trim()}
                />
              </Flex>
            ))}
          </Flex>
          {isLinkToCode && (
            <Flex vertical gap={12}>
              <span
                className={cn({
                  [styles.logicalActionsWrapperButton]: moreThanOneLogicalAction
                })}
              >
                <LinkButton
                  href={item.scmUrl ?? ""}
                  target="_blank"
                  icon={<ExternalLinkOutlined />}
                  size="small"
                  type="link"
                  className="sm-text-ks"
                >
                  {t("triage.drawer.dataFlow.seeInCode")}
                </LinkButton>
              </span>
              <Divider className={styles.logicalActionsWrapperDivider} />
            </Flex>
          )}
        </Flex>
      </LabelCollapse>
    </Flex>
  )
}
